import Modal from "react-bootstrap/Modal";
import { useNavigate } from "react-router-dom";
import { Button } from "react-bootstrap";

/**
 * @typedef {Object} SuccessModalProps
 * @property {boolean} showModal - Controls the visibility of the modal
 * @property {() => void} handleClose - Function to close the modal
 * @property {string} [message] - Success message to display
 * @property {string} [title] - Title of the modal
 * @property {string} [primaryButtonText] - Text for primary button
 * @property {string} [secondaryButtonText] - Text for secondary button
 * @property {string} [primaryButtonLink] - Navigation link for primary button
 * @property {string} [secondaryButtonLink] - Navigation link for secondary button
 * @property {() => void} [onPrimaryClick] - Optional callback for primary button
 * @property {() => void} [onSecondaryClick] - Optional callback for secondary button
 * @property {"success" | "error"} [type] - Type of the modal (success or error)
 * @property {()=> void} [handlePrimaryClickCallback] - Optional callback for primary button
 * @property {()=> void} [handleSecondaryClickCallback] - Optional callback for secondary button
 */

/**
 * A reusable success modal component.
 */
const SuccessModal = ({
  showModal,
  handleClose,
  message = "Operation completed successfully!",
  title = "Success",
  primaryButtonText = "Continue",
  secondaryButtonText,
  primaryButtonLink,
  secondaryButtonLink,
  onPrimaryClick,
  onSecondaryClick,
  type = "success",
  handleSecondaryClickCallback,
  handlePrimaryClickCallback,
  children,
}) => {
  const navigate = useNavigate();

  const handlePrimaryClick = () => {
    if (onPrimaryClick) {
      onPrimaryClick();
    } else if (primaryButtonLink) {
      if (handlePrimaryClickCallback) handlePrimaryClickCallback();
      navigate(primaryButtonLink);
    }
    if (handlePrimaryClickCallback) handlePrimaryClickCallback();
    handleClose();
  };

  const handleSecondaryClick = () => {
    if (onSecondaryClick) {
      onSecondaryClick();
    } else if (secondaryButtonLink) {
      navigate(secondaryButtonLink);
      if (handleSecondaryClickCallback) handleSecondaryClickCallback();
    }
    if (handleSecondaryClickCallback) handleSecondaryClickCallback();
    handleClose();
  };

  return (
    <Modal
      show={showModal}
      onHide={handleClose}
      centered
      className="custom-modal"
      backdrop="static"
      keyboard={false}
    >
      <Modal.Body className="p-2 m-4">
        {/* Success message */}
        <div className="text-center mb-2">
          <h5
            className={`text-${type === "success" ? "success" : "danger"} fw-bold fs-3`}
          >
            {title}
          </h5>
        </div>
        <p className="text-center mb-4 mt-2 pt-2 custom-modal-description ">
          {message}
        </p>

        {children && <div className="mb-4">{children}</div>}

        <div
          className={`d-flex flex-column flex-md-row gap-3 ${secondaryButtonText ? "justify-content-center" : "justify-content-center align-items-center"}`}
        >
          {secondaryButtonText && (
            <Button
              className="custom-cancel-button"
              onClick={handleSecondaryClick}
            >
              {secondaryButtonText}
            </Button>
          )}
          <Button
            className={`custom-primary-button ${!secondaryButtonText ? "w-auto" : ""}`}
            onClick={handlePrimaryClick}
          >
            {primaryButtonText}
          </Button>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default SuccessModal;
